import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import logo from "../images/sodtf logo dark horizontal.png"; // Adjust the path as necessary
import { auth, db } from "../../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AccountCircle } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

const getBannerBg = (type) => {
  const map = {
    warning: "#f59e0b", // amber
    danger: "#ef4444", // red
    error: "#ef4444",
    info: "#2563eb", // blue
    success: "#16a34a", // green
  };
  return map[String(type || "").toLowerCase()] || "#111827"; // slate fallback
};
function Navbar() {
  const [userRole, setUserRole] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [userData, setUserData] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [siteBanner, setSiteBanner] = useState(null);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { label: "Home", path: "/" },
    { label: "DTF Sheet Builder", path: "/dtf-sheet-builder" },
    { label: "Art Services", path: "/art-services" },
    { label: "Contact", path: "/contact" },
    { label: "Profile", path: "/profile" },
  ];

  const navigate = useNavigate();
  const location = useLocation();

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleProfileClick = () => {
    handleProfileMenuClose();
    navigate("/profile");
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Navbar logout error:", error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setUserRole(data.role);
          setUserEmail(user.email);
        }
      } else {
        setUserRole(null);
        setUserEmail(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "adminSettings", "siteBanner"),
      (snap) => setSiteBanner(snap.exists() ? snap.data() : null),
      (err) => {
        console.error("Navbar banner subscription failed:", err);
        if (err?.code === "permission-denied") setSiteBanner(null);
      }
    );
    return () => unsub();
  }, []);
  return (
    <>
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{ top: 0, width: "100%", zIndex: 5 }}
      >
        {siteBanner?.enabled && siteBanner?.message ? (
          <div
            style={{
              width: "100%",
              padding: "6px 12px",
              textAlign: "center",
              fontWeight: 600,
              background: getBannerBg(siteBanner.type),
              color: "#fff",
            }}
          >
            <span>{siteBanner.message}</span>
            {siteBanner.linkUrl ? (
              <a
                href={siteBanner.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  marginLeft: 12,
                  textDecoration: "underline",
                  color: "#fff",
                }}
              >
                {siteBanner.linkText || "Learn more"}
              </a>
            ) : null}
          </div>
        ) : null}
        {isMobile ? (
          <Toolbar
            sx={{
              justifyContent: "space-between",
              px: 0.5,
              py: 1,
              backgroundColor: "rgb(255,255,255)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "space-between",
                width: "100%",
                margin: "0 auto",
                marginTop: "0.5rem",
              }}
            >
              <Button
                onClick={handleDrawerToggle}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontWeight: "normal",
                  fontSize: "1.1rem",
                  textTransform: "none",
                  color: "#5A6872",
                  marginLeft: "5px",
                }}
              >
                {mobileOpen ? (
                  <CloseIcon sx={{ height: "2em", width: "2em" }} />
                ) : (
                  <MenuIcon sx={{ height: "2em", width: "2em" }} />
                )}
              </Button>
              <a href="/">
                <img src={logo} alt="Logo" height="80" />
              </a>
              {!userEmail ? (
                <Button
                  component={Link}
                  to="/login"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "normal",
                    fontSize: "3rem",
                    textTransform: "none",
                    color:
                      location.pathname === "/login" ? "#94C940" : "#5A6872",
                    "&:hover": { color: "#94C940" },
                  }}
                >
                  <AccountCircle sx={{ width: "2em", height: "2em" }} />
                </Button>
              ) : (
                <Button
                  onClick={handleProfileMenuOpen}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "normal",
                    fontSize: "1.1rem",
                    textTransform: "none",
                    color: "#5A6872",
                    marginRight: "5px",
                  }}
                >
                  <Avatar
                    src={
                      userData.profileImageURL ||
                      "https://cdn-icons-png.flaticon.com/512/6522/6522516.png"
                    }
                    alt="Profile"
                    sx={{ width: "50px", height: "50px" }}
                  />
                </Button>
              )}
            </Box>
            <Drawer
              anchor="left"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              ModalProps={{ keepMounted: true }}
              PaperProps={{
                sx: {
                  marginTop: "6rem",
                  height: "100%",
                  width: "100%",
                  backgroundColor: "#5A6872",
                  marginBottom: "2rem",
                },
              }}
            >
              <Box
                onClick={handleDrawerToggle}
                sx={{
                  textAlign: "center",
                  px: 2,
                  height: "stretch",
                  marginBottom: "1rem",
                }}
              >
                <List
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "stretch",
                    gap: 1.25, // ~10px vertical spacing
                    py: 2, // top/bottom padding for the whole list
                    px: 0,
                  }}
                >
                  {navItems.map((item) => (
                    <ListItem
                      key={item.path}
                      component={Link}
                      to={item.path}
                      sx={{
                        justifyContent: "center",
                        textAlign: "center",
                        border: "2px solid #94C940",
                        borderRadius: "0.75rem",
                        color: "#94C940",
                        fontFamily: "Anek latin",
                        textTransform: "uppercase",
                        fontSize: "1.25rem",
                        lineHeight: 1.2,
                        py: 1.25, // vertical padding per item
                        px: 2, // horizontal padding
                      }}
                    >
                      {item.label}
                    </ListItem>
                  ))}
                  {userRole === 1 ? (
                    <ListItem
                      key={"admin"}
                      component={Link}
                      to="/admin"
                      sx={{
                        justifyContent: "center",
                        textAlign: "center",
                        border: "2px solid #94C940",
                        borderRadius: "0.75rem",
                        color: "#94C940",
                        fontFamily: "Anek latin",
                        textTransform: "uppercase",
                        fontSize: "1.25rem",
                        lineHeight: 1.2,
                        py: 1.25,
                        px: 2,
                      }}
                    >
                      ADMIN
                    </ListItem>
                  ) : null}
                </List>
              </Box>
            </Drawer>
          </Toolbar>
        ) : (
          <Toolbar
            sx={{
              justifyContent: "space-between",
              flexDirection: "column",
              backgroundColor: "rgb(255,255,255)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                marginTop: 1,
                marginBottom: 1,
              }}
            >
              <a href="/">
                <img src={logo} alt="Logo" height="120" />
              </a>
            </Box>
            <Box
              sx={{
                color: "#5A6872",
                maxWidth: "80rem",
                width: "90%",
                borderBottom: "1.5px solid #5A6872",
                my: 0.5,
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "80%",
                maxWidth: "80rem",
                marginLeft: "20rem",
                marginRight: "20rem",
              }}
            >
              <Button
                component={Link}
                to="/"
                color="inherit"
                sx={{
                  color: location.pathname === "/" ? "#94C940" : "#5A6872",
                  fontFamily: "Anek Latin",
                  fontWeight: "5px",
                  fontSize: "1.1rem",
                  textTransform: "uppercase",
                  "&:hover": { color: "#94C940" },
                }}
              >
                Home
              </Button>
              <Button
                component={Link}
                to="/dtf-sheet-builder"
                color="#5A6872"
                sx={{
                  color:
                    location.pathname === "/dtf-sheet-builder"
                      ? "#94C940"
                      : "#5A6872",
                  fontFamily: "Anek Latin",
                  fontWeight: "5px",
                  fontSize: "1.1rem",
                  textTransform: "uppercase",
                  "&:hover": { color: "#94C940" },
                }}
              >
                DTF Sheet Builder
              </Button>
              <Button
                component={Link}
                to="/art-services"
                color="#5A6872"
                sx={{
                  color:
                    location.pathname === "/art-services"
                      ? "#94C940"
                      : "#5A6872",
                  fontFamily: "Anek Latin",
                  fontWeight: "5px",
                  fontSize: "1.1rem",
                  textTransform: "uppercase",
                  "&:hover": { color: "#94C940" },
                }}
              >
                Art Services
              </Button>
              <Button
                component={Link}
                to="/contact"
                color="#5A6872"
                sx={{
                  color:
                    location.pathname === "/contact" ? "#94C940" : "#5A6872",
                  fontFamily: "Anek Latin",
                  fontWeight: "5px",
                  fontSize: "1.1rem",
                  textTransform: "uppercase",
                  "&:hover": { color: "#94C940" },
                }}
              >
                Contact
              </Button>
              {userRole === 1 ? (
                <Button
                  component={Link}
                  to="/admin"
                  color="#5A6872"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "normal",
                    fontSize: "1.1rem",
                    textTransform: "none",
                    color:
                      location.pathname === "/admin" ? "#94C940" : "#5A6872",
                    "&:hover": { color: "#94C940" },
                  }}
                >
                  ADMIN
                </Button>
              ) : null}
              {!userEmail ? (
                <Button
                  component={Link}
                  to="/login"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "normal",
                    fontSize: "1.1rem",
                    textTransform: "none",
                    color:
                      location.pathname === "/login" ? "#94C940" : "#5A6872",
                    "&:hover": { color: "#94C940" },
                  }}
                >
                  Login | Sign Up
                  <AccountCircle sx={{ width: "1em", height: "1em" }} />
                </Button>
              ) : (
                <Button
                  onClick={handleProfileMenuOpen}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: "normal",
                    fontSize: "1.1rem",
                    textTransform: "none",
                    color:
                      location.pathname === "/profile" ? "#94C940" : "#5A6872",
                    "&:hover": { color: "#94C940" },
                  }}
                >
                  <Avatar
                    src={userData.profileImageURL || ""}
                    alt="Profile"
                    sx={{ width: "2.2em", height: "2.2em" }}
                  />
                </Button>
              )}
            </Box>
            <Box
              sx={{
                width: "90%",
                maxWidth: "80rem",
                borderBottom: "1.5px solid #5A6872",
                my: 0.5,
              }}
            />
          </Toolbar>
        )}
      </AppBar>
      <Menu
        anchorEl={profileMenuAnchor}
        open={Boolean(profileMenuAnchor)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        keepMounted
      >
        <MenuItem onClick={handleProfileClick} style={{ color: "#5A6872" }}>
          My profile
        </MenuItem>
        <MenuItem onClick={handleLogout} style={{ color: "#5A6872" }}>
          Sign out
        </MenuItem>
      </Menu>
      {/* Spacer to push down content below fixed navbar */}
      <Toolbar />
      <Box sx={{ height: { xs: 60, sm: 40, md: 180 } }} />
    </>
  );
}

export default Navbar;
